import { lazy, LazyExoticComponent, FC } from 'react';
import { routePath, authPaths } from 'config/route-config';

const AuthResponse = import('components/app/routes/AuthResponse');
import DirectSalesLanding from 'components/direct-sales-landing/DirectSalesLanding';
import EnergyNeeds from 'components/direct-sales/energy-needs/EnergyNeeds';
import EvTariffInformation from 'components/direct-sales/ev-tariff-information-revamp/EvTariffInformation';
import DirectDebit from 'components/direct-sales/direct-debit-revamp/DirectDebit';
import MeterpointValidation from 'components/direct-sales/meterpoint-validation/MeterPointValidationRevamp';
import MeterpointGuide from 'components/direct-sales/meterpoint-guide/MeterpointGuideRevamp';
import SelectOptions from 'components/direct-sales/select-options/SelectOptionsRevamp';
import BusinessAnnouncement from 'components/direct-sales/business-updates-revamp/BusinessAnnouncement';
import SubmitEmail from 'components/direct-sales/business-updates-revamp/SubmitEmail';
import SubmitEmailSuccess from 'components/direct-sales/business-updates-revamp/SubmitEmailSuccess';

const PrepayInfo = import('components/direct-sales/prepay-info/PrepayInfo');

const PersonalInformationSummary = import(
  'components/direct-sales/personal-information-summary/PersonalInformationSummary'
);
const Quote = import('components/direct-sales/quote/Quote');
const EmailQuote = import('components/direct-sales/email-quote/EmailQuote');
const CreateAccount = import(
  'components/direct-sales/create-account/CreateAccount'
);

const DirectDebitType = import(
  'components/direct-sales/direct-debit-type/DirectDebitType'
);
const PsrEntry = import('components/direct-sales/psr-entry/PsrEntry');
const PsrCapture = import('components/direct-sales/psr-capture/PsrCapture');
const MarketingPreferences = import(
  'components/direct-sales/user-preferences/UserPreferences'
);

const OrderSummary = import(
  'components/direct-sales/order-summary/OrderSummary'
);
const OrderComplete = import(
  'components/direct-sales/order-complete/OrderComplete'
);

type Component = FC | LazyExoticComponent<FC>;

export type Route = {
  path: string;
  name: string;
  step?: number;
  featureFlag?: string;
  isLanding?: boolean;
  isEnding?: boolean;
  requiresLogin?: boolean;
  noIndex?: boolean;
  Content?: Component;
  RightSidebar?: Component;
  LeftSidebar?: Component;
  Header?: Component;
  Footer?: Component;
};

export const authRoutes = [
  {
    name: 'Auth Response',
    path: authPaths.authResponse,
    Content: lazy(() => AuthResponse),
    isLanding: true,
    noIndex: true
  }
];

export const partnerLandingRoutes: Route[] = [
  {
    name: 'Direct Sales',
    path: routePath.home,
    step: 1,
    isLanding: true,
    Content: DirectSalesLanding.Content,
    RightSidebar: DirectSalesLanding.RightSidebar,
    Header: DirectSalesLanding.Header,
    Footer: DirectSalesLanding.Footer
  }
];

export const journeyRoutes: Route[] = [
  {
    name: 'Select Options',
    path: routePath.selectOptions,
    Content: SelectOptions.Content,
    Header: SelectOptions.Header
  },
  {
    name: 'Your energy needs',
    path: routePath.energyNeeds,
    step: 1,
    Content: EnergyNeeds.Content,
    RightSidebar: EnergyNeeds.RightSidebar,
    Header: EnergyNeeds.Header,
    Footer: EnergyNeeds.Footer
  },
  {
    name: 'Meterpoint Validation',
    path: routePath.meterpointValidation,
    step: 1,
    Content: MeterpointValidation.Content,
    RightSidebar: EnergyNeeds.RightSidebar,
    Header: EnergyNeeds.Header,
    Footer: EnergyNeeds.Footer
  },
  {
    name: 'Meterpoint Guide',
    path: routePath.meterpointGuide,
    Content: MeterpointGuide.Content,
    step: 1,
    RightSidebar: EnergyNeeds.RightSidebar,
    Header: EnergyNeeds.Header,
    Footer: EnergyNeeds.Footer
  },
  {
    name: 'EV Tariff Information',
    path: routePath.evTariffInformation,
    step: 1,
    Content: EvTariffInformation.Content,
    RightSidebar: EvTariffInformation.RightSidebar,
    Header: EvTariffInformation.Header,
    Footer: EvTariffInformation.Footer
  },
  {
    name: 'Business Announcement',
    path: routePath.businessUpdatesAnnouncement,
    Content: BusinessAnnouncement.Content,
    Header: BusinessAnnouncement.Header
  },
  {
    name: 'Email Submit',
    path: routePath.businessUpdatesSubmitEmail,
    Content: SubmitEmail.Content,
    Header: SubmitEmail.Header
  },
  {
    name: 'Email Submit Success',
    path: routePath.businessUpdatesSubmitEmailSuccess,
    Content: SubmitEmailSuccess.Content
  },
  {
    name: 'Personal Information Summary',
    path: routePath.personalInformationSummary,
    Content: lazy(() => PersonalInformationSummary)
  },
  {
    name: 'Quote',
    path: routePath.quote,
    Content: lazy(() => Quote)
  },
  {
    name: 'Email Quote',
    path: routePath.emailQuote,
    Content: lazy(() => EmailQuote)
  },
  {
    name: 'Create account',
    path: routePath.createAccount,
    Content: lazy(() => CreateAccount)
  },
  {
    name: 'Prepay Info',
    path: routePath.prepayInfo,
    Content: lazy(() => PrepayInfo)
  },
  {
    name: 'Direct debit type',
    path: routePath.directDebitType,
    Content: lazy(() => DirectDebitType)
  },
  {
    name: 'Account set up',
    path: routePath.directDebit,
    step: 3,
    Content: DirectDebit.Content,
    RightSidebar: DirectDebit.RightSidebar,
    Header: DirectDebit.Header,
    Footer: DirectDebit.Footer
  },
  {
    name: 'Psr entry',
    path: routePath.psrEntry,
    Content: lazy(() => PsrEntry)
  },
  {
    name: 'Psr capture',
    path: routePath.psrCapture,
    Content: lazy(() => PsrCapture)
  },
  {
    name: 'Marketing preferences',
    path: routePath.userPreferences,
    Content: lazy(() => MarketingPreferences)
  },
  {
    name: 'Order summary',
    path: routePath.orderSummary,
    Content: lazy(() => OrderSummary)
  },
  {
    name: 'Order complete',
    path: routePath.orderComplete,
    Content: lazy(() => OrderComplete)
  }
];

export const routes = [
  ...journeyRoutes,
  ...partnerLandingRoutes,
  ...authRoutes
];
